import { replayFromSite, watchGoal } from '../game2';
import { setUserUploaded } from '../slices/mainModeSlice';
import { setStats } from '../slices/gameStatsSlice';
import { setHeadlessReplayContext } from '../components/Home';
import { startCanvasRecording } from '../utils/recorder';

let installed = false;
let storeDispatch = null;
let storeGetState = null;
let thawedMatches = null;

const DEFAULT_TIMEOUT_MS = 120000;

function safeNonNegativeInteger(value) {
  if (value === null || value === undefined || typeof value === 'boolean') return false;
  if (typeof value === 'string' && value.trim() === '') return false;
  return Number.isSafeInteger(Number(value)) && Number(value) >= 0;
}

function base64ToArrayBuffer(base64) {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

export function setupHeadlessBridge(store) {
  if (typeof window === 'undefined' || installed) return;
  installed = true;
  window.__HRA_HEADLESS_MODE__ = true;
  // Disable WebGL-dependent TensorFlow backends when running headless to avoid GPU init failures.
  window.__HRA_FORCE_CPU__ = true;
  try {
    const doc = window.document;
    if (doc) {
      let targetRoot = doc.documentElement;
      if (!targetRoot) {
        targetRoot = doc.createElement('html');
        doc.appendChild?.(targetRoot);
      }

      let body = doc.body;
      if (!body) {
        body = doc.createElement('body');
        targetRoot?.appendChild?.(body);
      }

      if (!doc.body) {
        try {
          Object.defineProperty(doc, 'body', {
            configurable: true,
            enumerable: true,
            get: () => body,
            set: () => {}
          });
        } catch (_) {
          // ignore defineProperty failures; fall back to best-effort DOM access
        }
      }
    }
  } catch (_) {
    // ignore DOM hydration patch failures; downstream code will surface actionable errors if the body remains unavailable
  }
  const { dispatch, getState } = store;
  storeDispatch = dispatch;
  storeGetState = getState;

  async function parseReplayFromBuffer(params) {
    const {
      buffer,
      replayId,
      replayName,
      lastModified,
      captureBff = false,
      captureStride = 1,
      captureLimit = 100000,
      captureWindows = null,
      timeoutMs = DEFAULT_TIMEOUT_MS,
      captureReturnFrames = true,
      // A second, capture-only pass reuses the authoritative stats sidecar.
      // It must not wait for a duplicate network POST before returning.
      statsAvailable = false,
      capturePreTicks = 240,
      capturePostTicks = 220
    } = params || {};
    if (!buffer) throw new Error('Missing replay buffer');
    if (!replayId) throw new Error('Missing replayId');
    if (captureBff === true && !Array.isArray(captureWindows)) {
      throw new Error('Capture windows are required for bounded BFF capture');
    }

    const arrayBuffer = typeof buffer === 'string' ? base64ToArrayBuffer(buffer) : buffer;
    window.__HRA_HEADLESS_STATUS__ = { status: 'processing', replayId, timestamp: Date.now() };
    const context = {
      replayId,
      replayName: replayName || `${replayId}.hbr2`,
      lastModified: lastModified || Date.now()
    };
    // Full physics capture is opt-in because a replay can contain tens of
    // thousands of frames. The game loop records each frame before applying
    // queued events, making these safe pre-action snapshots.
    window.__HRA_BFF_CAPTURE_ACTIVE__ = captureBff === true;
    window.__HRA_BFF_CAPTURE_STRIDE__ = Number.isFinite(Number(captureStride)) ? Math.max(1, Number(captureStride)) : 1;
    window.__HRA_BFF_CAPTURE_LIMIT__ = Number.isFinite(Number(captureLimit)) ? Math.max(1, Math.floor(Number(captureLimit))) : 100000;
    window.__HRA_BFF_CAPTURE_FRAMES__ = captureBff === true ? [] : null;
    window.__HRA_BFF_CAPTURE_REPLAY_ID__ = replayId;
    window.__HRA_BFF_CAPTURE_STADIUMS__ = captureBff === true ? {} : null;
    window.__HRA_BFF_CAPTURE_STADIUM_OBJECT_KEYS__ = captureBff === true ? new WeakMap() : null;
    window.__HRA_BFF_CAPTURE_STADIUM_KEY_COUNTER__ = 0;
    // A capture that reaches its frame limit is incomplete even when the
    // parser itself finishes cleanly. Keep this state in the browser until
    // finalize() serializes the capture header.
    window.__HRA_BFF_CAPTURE_LIMIT_REACHED__ = false;
    window.__HRA_BFF_CAPTURE_WINDOWS__ = Array.isArray(captureWindows)
      ? captureWindows
        .filter(window => window && safeNonNegativeInteger(window.matchIndex)
          && safeNonNegativeInteger(window.startTick) && safeNonNegativeInteger(window.endTick)
          && Number(window.endTick) >= Number(window.startTick))
        .map(window => ({
          matchIndex: Number(window.matchIndex),
          coordinate: window.coordinate === 'replay' ? 'replay' : 'local',
          startTick: Math.max(0, Number(window.startTick)),
          endTick: Math.max(Number(window.startTick), Number(window.endTick)),
        }))
      : null;
    window.__HRA_BFF_CAPTURE_MATCH_START_TICKS__ = {};
    window.__HRA_PARSING_DONE__ = false;
    setHeadlessReplayContext(context);
    dispatch(setUserUploaded(true));

    let originalFetch = null;
    let statsPosted = statsAvailable === true;
    let resolved = false;
    let timeoutId = null;
    let controlsCheckId = null;

    const cleanup = () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
        timeoutId = null;
      }
      if (controlsCheckId) {
        window.clearInterval(controlsCheckId);
        controlsCheckId = null;
      }
      if (originalFetch) {
        window.fetch = originalFetch;
        originalFetch = null;
      }
      window.__HRA_BFF_CAPTURE_ACTIVE__ = false;
      window.__HRA_BFF_CAPTURE_WINDOWS__ = null;
      window.__HRA_BFF_CAPTURE_MATCH_START_TICKS__ = null;
      window.__HRA_BFF_CAPTURE_STADIUMS__ = null;
      window.__HRA_BFF_CAPTURE_STADIUM_OBJECT_KEYS__ = null;
      window.__HRA_BFF_CAPTURE_LIMIT_REACHED__ = null;
      if (unsubscribe) {
        unsubscribe();
        unsubscribe = null;
      }
    };

    const finalize = (status) => {
    if (resolved) return;
    resolved = true;
    const bffCapture = captureBff === true && Array.isArray(window.__HRA_BFF_CAPTURE_FRAMES__)
      ? { schema: 'bff-physics-capture-v2', captureVersion: 2,
          capturePreTicks: Number.isFinite(Number(capturePreTicks)) ? Math.max(0, Math.floor(Number(capturePreTicks))) : 240,
          capturePostTicks: Number.isFinite(Number(capturePostTicks)) ? Math.max(0, Math.floor(Number(capturePostTicks))) : 220,
          captureComplete: true,
          stride: window.__HRA_BFF_CAPTURE_STRIDE__, limit: window.__HRA_BFF_CAPTURE_LIMIT__, frameCount: window.__HRA_BFF_CAPTURE_FRAMES__.length,
          truncated: window.__HRA_BFF_CAPTURE_LIMIT_REACHED__ === true,
          limitReached: window.__HRA_BFF_CAPTURE_LIMIT_REACHED__ === true,
          windowed: Array.isArray(window.__HRA_BFF_CAPTURE_WINDOWS__),
          windows: Array.isArray(window.__HRA_BFF_CAPTURE_WINDOWS__) ? cloneValue(window.__HRA_BFF_CAPTURE_WINDOWS__) : null,
          stadiums: window.__HRA_BFF_CAPTURE_STADIUMS__ && typeof window.__HRA_BFF_CAPTURE_STADIUMS__ === 'object'
            ? cloneValue(window.__HRA_BFF_CAPTURE_STADIUMS__) : {},
          ...(captureReturnFrames === true ? { frames: cloneValue(window.__HRA_BFF_CAPTURE_FRAMES__) } : {}) }
      : null;
    cleanup();
    window.__HRA_HEADLESS_STATUS__ = { status, replayId, timestamp: Date.now() };
    if (bffCapture) window.__HRA_HEADLESS_STATUS__.bffCapture = bffCapture;
    try {
      if (typeof window.__HRA_HEADLESS_RESTORE_IMMUTABLE__ === 'function') {
        window.__HRA_HEADLESS_RESTORE_IMMUTABLE__();
      }
    } catch (_) {}
  };

    originalFetch = window.fetch.bind(window);
    window.fetch = async (...args) => {
      const requestUrl = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url);
      const requestInit = args[1] || {};
      const requestMethod = String(requestInit.method || (args[0] && args[0].method) || 'GET').toUpperCase();
      // The capture pass replays the file only to collect native frames. Do
      // not submit a second stats payload and overwrite the first pass's
      // source hash or human review labels.
      if (statsAvailable === true && requestMethod === 'POST'
        && typeof requestUrl === 'string' && requestUrl.includes('/api/stats/')) {
        return new Response(JSON.stringify({ ok: true, capturePass: true }), {
          status: 200,
          headers: { 'Content-Type': 'application/json' }
        });
      }
      const response = await originalFetch(...args);
      try {
        if (typeof requestUrl === 'string' && requestUrl.includes('/api/stats/')) {
          statsPosted = true;
          if (window.__HRA_PARSING_DONE__) finalize('stats-posted');
        }
      } catch (_) {
        // ignore
      }
      return response;
    };

    let unsubscribe = null;
    // Removed store subscription that was finalizing prematurely

    const parseTimeoutMs = Number.isFinite(Number(timeoutMs)) ? Math.max(1000, Number(timeoutMs)) : DEFAULT_TIMEOUT_MS;
    timeoutId = window.setTimeout(() => {
      finalize('timeout');
    }, parseTimeoutMs);

    try {
      replayFromSite(arrayBuffer);
    } catch (error) {
      cleanup();
      throw error;
    }

    const hasReduxStats = () => {
      try {
        const matches = getState()?.gameStats?.matches;
        return Array.isArray(matches) && matches.length > 0;
      } catch (_) {
        return false;
      }
    };

    const checkReplayReady = () => {
      if (resolved) return;
      // Stats can be posted before the replay engine finishes its final tick.
      // Wait for both signals so the capture includes the complete trajectory.
      if (window.__HRA_PARSING_DONE__ && (statsPosted || statsAvailable === true)) {
        finalize(statsPosted ? 'stats-posted' : 'ready');
      } else if (window.__HRA_PARSING_DONE__ && hasReduxStats()) {
        // Local/offline runs may have no reachable POST endpoint, while the
        // Redux reducer still has the complete authoritative match object.
        finalize('stats-fallback');
      }
    };
    controlsCheckId = window.setInterval(checkReplayReady, 200);

    while (!resolved) {
      await new Promise((resolve) => setTimeout(resolve, 200));
    }

    const status = window.__HRA_HEADLESS_STATUS__;
    try {
      if (status?.status === 'stats-posted' || status?.status === 'stats-fallback' || status?.status === 'ready') {
        const state = getState();
        if (state?.gameStats?.matches) {
          thawedMatches = cloneMatches(state.gameStats.matches);
          // Return the parsed sidecar as part of the headless result. The
          // stats POST is best-effort (and is often unavailable when the
          // parser is run against a local build), while the replay engine has
          // already produced an authoritative copy in the Redux store.
          // Keeping this fallback here lets capture-only jobs remain
          // reproducible without fabricating events from the physics frames.
          status.matches = cloneMatches(thawedMatches);
        }
      }
    } catch (_) {}
    try {
      const teams = collectHeadlessTeamsSnapshot();
      if (teams) {
        status.teams = teams;
      }
    } catch (_) {}
    return status;
  }

  function drainBffCaptureChunk(chunkSize = 1000) {
    const frames = window.__HRA_BFF_CAPTURE_FRAMES__;
    if (!Array.isArray(frames)) return { frames: [], remaining: 0, done: true };
    const size = Number.isFinite(Number(chunkSize)) ? Math.max(1, Math.floor(Number(chunkSize))) : 1000;
    const chunk = frames.splice(0, size);
    return { frames: cloneValue(chunk), remaining: frames.length, done: frames.length === 0 };
  }

  window.__HRA_HEADLESS_STATUS__ = { status: 'idle' };
  window.__HRA_HEADLESS__ = {
    parseReplay: parseReplayFromBuffer,
    drainBffCaptureChunk,
    recordClip: recordClipHeadless,
    collectTeams: collectHeadlessTeamsSnapshot
  };
  window.__HRA_HEADLESS_READY__ = true;
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function ensureReplayControlsReady() {
  return Boolean(document.querySelector('[data-hook="play"]'));
}

function readPlaybackSpeed() {
  const display = document.querySelector('[data-hook="spd"]');
  if (!display) return null;
  const raw = (display.textContent || '').replace(/[^0-9.]/g, '');
  if (!raw) return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

async function setPlaybackSpeed(targetSpeed) {
  if (!Number.isFinite(targetSpeed) || targetSpeed <= 0) return false;
  const speedDisplay = document.querySelector('[data-hook="spd"]');
  const speedUp = document.querySelector('[data-hook="spdup"]');
  const speedDown = document.querySelector('[data-hook="spddn"]');
  if (!speedDisplay || !speedUp || !speedDown) return false;

  let current = readPlaybackSpeed();
  if (current === null) return false;

  const maxAttempts = 20;
  let attempts = 0;
  while (Math.abs(current - targetSpeed) > 0.01 && attempts < maxAttempts) {
    if (current < targetSpeed) {
      speedUp.click();
    } else {
      speedDown.click();
    }
    attempts += 1;
    await sleep(120);
    const updated = readPlaybackSpeed();
    if (updated === null) break;
    current = updated;
  }

  const success = Math.abs(current - targetSpeed) <= 0.05;
  console.log('Headless: playback speed', current, 'target', targetSpeed, 'success', success);
  return success;
}

function ensureReplayPlaying() {
  try {
    const playIcon = document.querySelector('[data-hook="playicon"]');
    const playButton = document.querySelector('[data-hook="play"]');
    if (!playIcon || !playButton) return false;
    const state = (playIcon.textContent || '').trim();
    if (state !== '⏸') {
      playButton.click();
      return true;
    }
  } catch (_) {}
  return false;
}

function ensureReplayPaused() {
  try {
    const playIcon = document.querySelector('[data-hook="playicon"]');
    const playButton = document.querySelector('[data-hook="play"]');
    if (!playIcon || !playButton) return false;
    const state = (playIcon.textContent || '').trim();
    if (state === '⏸') {
      playButton.click();
      return true;
    }
  } catch (_) {}
  return false;
}

function getReplayCanvas() {
  const selectors = [
    '#game-state-view .canvas canvas',
    '#game-state-view canvas',
    '[data-hook="canvas"] canvas',
    '#game-canvas canvas',
    '.game-canvas canvas'
  ];
  for (const selector of selectors) {
    const found = document.querySelector(selector);
    if (found) return found;
  }
  const canvases = Array.from(document.querySelectorAll('canvas'));
  if (!canvases.length) return null;
  const sizable = canvases.find((node) => {
    const rect = node.getBoundingClientRect();
    return rect.width >= 400 && rect.height >= 200;
  });
  return sizable || canvases[0];
}

async function waitForReplayCanvas(timeoutMs = 5000) {
  const started = Date.now();
  let lastCanvas = null;
  let lastMetrics = null;
  while (Date.now() - started < timeoutMs) {
    const canvas = getReplayCanvas();
    if (canvas) {
      lastCanvas = canvas;
      lastMetrics = readCanvasMetrics(canvas);
      if (lastMetrics?.ready) {
        return canvas;
      }
    }
    await sleep(120);
  }
  if (lastCanvas && lastMetrics && typeof console !== 'undefined') {
    try {
      console.warn('Headless: replay canvas readiness timed out', lastMetrics);
    } catch (_) {}
  }
  return lastCanvas;
}

function readCanvasMetrics(canvas) {
  if (!canvas) return null;
  const metrics = {
    width: Number(canvas.width) || 0,
    height: Number(canvas.height) || 0,
    styleWidth: null,
    styleHeight: null,
    rectWidth: 0,
    rectHeight: 0
  };
  try {
    metrics.styleWidth = canvas.style?.width || null;
    metrics.styleHeight = canvas.style?.height || null;
  } catch (_) {}
  try {
    if (typeof canvas.getBoundingClientRect === 'function') {
      const rect = canvas.getBoundingClientRect();
      metrics.rectWidth = Math.round(rect?.width || 0);
      metrics.rectHeight = Math.round(rect?.height || 0);
    }
  } catch (_) {}
  metrics.ready = (
    (metrics.width >= 360 && metrics.height >= 180) ||
    (metrics.rectWidth >= 360 && metrics.rectHeight >= 180)
  );
  return metrics;
}

function readTimerSeconds() {
  const timer = document.querySelector('[data-hook="timer"]');
  if (!timer) return null;
  const raw = (timer.textContent || '').trim();
  if (!raw) return null;
  const main = raw.includes('/') ? raw.split('/')[0].trim() : raw;
  const segments = main.split(':').map((part) => part.trim()).filter(Boolean);
  if (segments.length === 2) {
    const mins = Number(segments[0]);
    const secs = Number(segments[1]);
    if (Number.isFinite(mins) && Number.isFinite(secs)) {
      return mins * 60 + secs;
    }
  }
  if (segments.length === 3) {
    const hours = Number(segments[0]);
    const mins = Number(segments[1]);
    const secs = Number(segments[2]);
    if (Number.isFinite(hours) && Number.isFinite(mins) && Number.isFinite(secs)) {
      return hours * 3600 + mins * 60 + secs;
    }
  }
  const numeric = Number(main.replace(/[^0-9.:-]/g, ''));
  return Number.isFinite(numeric) ? numeric : null;
}

function findTimelineSlider() {
  const candidates = Array.from(document.querySelectorAll('input[type="range"]'));
  if (!candidates.length) return null;
  const preferred = candidates.find((el) => el.dataset?.hook === 'progress');
  if (preferred) return preferred;
  const timebar = candidates.find((el) => el.closest('.timebar'));
  if (timebar) return timebar;
  return candidates[0];
}

let cachedTimelineMeta = null;

async function ensureTimelineMeta() {
  const slider = findTimelineSlider();
  if (!slider) return null;
  if (cachedTimelineMeta && cachedTimelineMeta.slider === slider) return cachedTimelineMeta;

  const originalValue = slider.value;
  const originalMin = Number(slider.min || 0);
  const originalMax = Number(slider.max || 0);

  slider.value = slider.max || slider.value;
  slider.dispatchEvent(new Event('input', { bubbles: true }));
  slider.dispatchEvent(new Event('change', { bubbles: true }));
  await sleep(250);
  const totalSeconds = readTimerSeconds();

  // restore original position
  slider.value = originalValue;
  slider.dispatchEvent(new Event('input', { bubbles: true }));
  slider.dispatchEvent(new Event('change', { bubbles: true }));
  await sleep(150);

  cachedTimelineMeta = {
    slider,
    min: originalMin,
    max: originalMax,
    totalSeconds: Number.isFinite(totalSeconds) ? totalSeconds : null
  };
  return cachedTimelineMeta;
}

async function seekToSeconds(seconds) {
  const meta = await ensureTimelineMeta();
  if (!meta || !meta.slider) return false;
  const safeSeconds = Number.isFinite(seconds) && seconds >= 0 ? seconds : 0;
  let targetValue;
  if (meta.totalSeconds && meta.totalSeconds > 0) {
    const ratio = Math.max(0, Math.min(1, safeSeconds / meta.totalSeconds));
    const span = meta.max - meta.min;
    targetValue = meta.min + ratio * (span || 0);
  } else if (meta.max > meta.min) {
    const span = meta.max - meta.min;
    targetValue = meta.min + Math.max(0, Math.min(1, safeSeconds / span)) * span;
  } else {
    targetValue = safeSeconds;
  }

  meta.slider.value = targetValue;
  meta.slider.dispatchEvent(new Event('input', { bubbles: true }));
  meta.slider.dispatchEvent(new Event('change', { bubbles: true }));
  await sleep(150);
  return true;
}

async function blobToBase64(blob) {
  const buffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

async function recordClipHeadless(options = {}) {
  if (window.__HRA_HEADLESS_CLIP_ACTIVE__) {
    return { status: 'error', error: 'Clip recorder already running' };
  }
  if (!ensureReplayControlsReady()) {
    return { status: 'error', error: 'Replay controls unavailable' };
  }

  window.__HRA_HEADLESS_CLIP_ACTIVE__ = true;
  let restoreImmutability = null;
  try {
    restoreImmutability = disableImmutabilityGlobals();

    if (storeDispatch && thawedMatches) {
      try {
        storeDispatch(setStats(cloneMatches(thawedMatches)));
      } catch (_) {}
    }

    const goalIndex = Number.isFinite(options.goalIndex) ? Number(options.goalIndex) : null;
    const seekSeconds = Number.isFinite(options.seekSeconds) ? Number(options.seekSeconds) : null;
    const preSeconds = Number.isFinite(options.preSeconds) ? Math.max(0, options.preSeconds) : 5;
    const postSeconds = Number.isFinite(options.postSeconds) ? Math.max(0, options.postSeconds) : 10;
    const totalDurationMs = Math.round((preSeconds + postSeconds) * 1000);
    const preferMp4 = options.preferMp4 !== false;
    const width = Number.isFinite(options.width) && options.width > 0 ? Number(options.width) : null;
    const height = Number.isFinite(options.height) && options.height > 0 ? Number(options.height) : null;
    const playbackSpeed = Number.isFinite(options.playbackSpeed) && options.playbackSpeed > 0 ? Number(options.playbackSpeed) : 1;

    ensureReplayPaused();

    if (Number.isInteger(goalIndex) && goalIndex >= 0) {
      try { watchGoal(goalIndex); } catch (_) {}
      await sleep(450);
    }

    if (playbackSpeed !== 1) {
      const adjusted = await setPlaybackSpeed(playbackSpeed);
      if (!adjusted) {
        console.warn('Headless: failed to adjust playback speed to', playbackSpeed);
      }
    } else {
      await setPlaybackSpeed(1);
    }

    let startSeconds = seekSeconds;
    if (!Number.isFinite(startSeconds)) {
      const current = readTimerSeconds();
      if (Number.isFinite(current)) {
        startSeconds = Math.max(0, current - preSeconds);
      }
    } else {
      startSeconds = Math.max(0, startSeconds - preSeconds);
    }

    if (Number.isFinite(startSeconds)) {
      await seekToSeconds(startSeconds);
      await sleep(250);
    }

    if (ensureReplayPaused()) {
      // pause command toggles state, ensure slider remains
      await sleep(150);
    }

    const canvas = await waitForReplayCanvas(7000);
    if (!canvas) {
      return { status: 'error', error: 'Replay canvas not available' };
    }
    try {
      const metrics = readCanvasMetrics(canvas) || {};
      console.log('Headless: canvas metrics', {
        ...metrics,
        tag: canvas.tagName,
        className: canvas.className
      });
    } catch (_) {}

    let stopRecorder = null;
    const clipPromise = new Promise((resolve, reject) => {
      try {
        const recorder = startCanvasRecording({
          canvas,
          fps: 30,
          preferMp4,
          width,
          height,
          onStop: resolve
        });
        stopRecorder = () => { try { recorder.stop(); } catch (_) {} };
      } catch (error) {
        reject(error);
      }
    });

    const playing = ensureReplayPlaying();
    if (!playing) {
      console.warn('Headless: replay play toggle not triggered automatically; attempting manual speed sync');
    }
    if (playbackSpeed !== 1) {
      await setPlaybackSpeed(playbackSpeed);
    } else {
      await setPlaybackSpeed(1);
    }
    await sleep(totalDurationMs);
    if (typeof stopRecorder === 'function') {
      stopRecorder();
    }
    const recording = await clipPromise;
    ensureReplayPaused();

    if (!recording?.blob) {
      return { status: 'error', error: 'Clip recorder returned no data' };
    }

    if (typeof recording.blob.size === 'number' && recording.blob.size === 0) {
      return { status: 'error', error: 'Clip recorder produced an empty blob' };
    }

    const base64 = await blobToBase64(recording.blob);
    const mime = recording.mime || (preferMp4 ? 'video/mp4' : 'video/webm');
    const ext = recording.ext || (preferMp4 ? 'mp4' : 'webm');

    return {
      status: 'ok',
      base64,
      mime,
      ext,
      durationSeconds: (preSeconds + postSeconds),
      meta: {
        goalIndex,
        seekSeconds,
        preSeconds,
        postSeconds
      }
    };
  } catch (error) {
    return { status: 'error', error: error?.message || String(error) };
  } finally {
    window.__HRA_HEADLESS_CLIP_ACTIVE__ = false;
    try {
      if (typeof restoreImmutability === 'function') {
        restoreImmutability();
      }
    } catch (_) {}
  }
}

function captureImmutabilityGlobals() {
  return {
    freeze: Object.freeze,
    seal: Object.seal,
    preventExtensions: typeof Object.preventExtensions === 'function' ? Object.preventExtensions : null,
    defineProperty: Object.defineProperty,
    defineProperties: Object.defineProperties,
    reflectDefineProperty: typeof Reflect !== 'undefined' && typeof Reflect.defineProperty === 'function'
      ? Reflect.defineProperty
      : null
  };
}

function disableImmutabilityGlobals() {
  const snapshot = captureImmutabilityGlobals();
  const identity = (value) => value;

  try { Object.freeze = identity; } catch (_) {}
  try { Object.seal = identity; } catch (_) {}
  if (snapshot.preventExtensions) {
    try { Object.preventExtensions = identity; } catch (_) {}
  }

  return () => {
    try { Object.freeze = snapshot.freeze; } catch (_) {}
    try { Object.seal = snapshot.seal; } catch (_) {}
    if (snapshot.preventExtensions) {
      try { Object.preventExtensions = snapshot.preventExtensions; } catch (_) {}
    }
  };
}

function cloneMatches(matches) {
  if (!Array.isArray(matches)) return null;
  return matches.map((match) => cloneValue(match));
}

function cloneValue(value) {
  if (value === undefined || value === null) return value;
  if (typeof structuredClone === 'function') {
    try {
      return structuredClone(value);
    } catch (_) {}
  }
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (_) {}
  if (Array.isArray(value)) {
    return value.map((item) => cloneValue(item));
  }
  if (typeof value === 'object') {
    const clone = {};
    for (const key of Object.keys(value)) {
      clone[key] = cloneValue(value[key]);
    }
    return clone;
  }
  return value;
}

function collectHeadlessTeamsSnapshot() {
  const matches = resolveMatchesSnapshot();
  if (!Array.isArray(matches) || matches.length === 0) {
    return { red: [], blue: [], source: 'headless', matchCount: 0, ready: false };
  }
  const teams = collectTeamsFromMatches(matches);
  const ready = Array.isArray(teams.red) && teams.red.length > 0 && Array.isArray(teams.blue) && teams.blue.length > 0;
  return {
    red: teams.red,
    blue: teams.blue,
    source: 'headless',
    matchCount: matches.length,
    ready
  };
}

function resolveMatchesSnapshot() {
  if (Array.isArray(thawedMatches) && thawedMatches.length) {
    return cloneMatches(thawedMatches);
  }
  try {
    const state = typeof storeGetState === 'function' ? storeGetState() : null;
    const matches = state?.gameStats?.matches;
    if (Array.isArray(matches) && matches.length) {
      return cloneMatches(matches);
    }
  } catch (_) {}
  return null;
}

function collectTeamsFromMatches(matches) {
  if (!Array.isArray(matches) || matches.length === 0) {
    return { red: [], blue: [] };
  }

  const assignments = new Map();
  matches.forEach((match) => harvestTeamsFromMatch(match, assignments));

  let result = finalizeTeamAssignments(assignments);
  if (result.red.length && result.blue.length) {
    return result;
  }

  fillTeamsFromPlayersList(matches, assignments);
  result = finalizeTeamAssignments(assignments);
  return result;
}

function harvestTeamsFromMatch(match, assignments) {
  if (!match || typeof match !== 'object') return;

  recordTeamCollection(assignments, match.redTeam, 'red');
  recordTeamCollection(assignments, match.blueTeam, 'blue');
  recordTeamCollection(assignments, match.redPlayers, 'red');
  recordTeamCollection(assignments, match.bluePlayers, 'blue');

  recordPlayerEntries(assignments, match.player);
  recordPlayerEntries(assignments, match.players);

  if (match.teams && typeof match.teams === 'object') {
    recordTeamCollection(assignments, match.teams.red || match.teams.redTeam || match.teams.home || match.teams.homeTeam || match.teams.team1, 'red');
    recordTeamCollection(assignments, match.teams.blue || match.teams.blueTeam || match.teams.away || match.teams.awayTeam || match.teams.team2, 'blue');
  }

  const kicks = Array.isArray(match?.kicks) ? match.kicks : [];
  kicks.forEach((kick) => recordTeamAssignment(assignments, kick?.player, kick?.team));

  const shots = Array.isArray(match?.shots) ? match.shots : [];
  shots.forEach((shot) => {
    const teamLabel = shot?.team ?? shot?.scorerTeam ?? shot?.shotTeam;
    recordTeamAssignment(assignments, shot?.player ?? shot?.scorer, teamLabel);
    recordTeamAssignment(assignments, shot?.saver ?? shot?.savedBy, shot?.savedByTeam);
  });

  const goals = Array.isArray(match?.goals) ? match.goals : [];
  goals.forEach((goal) => {
    const teamLabel = goal?.shot?.team ?? goal?.team ?? goal?.scoringTeam;
    recordTeamAssignment(assignments, goal?.scorer, teamLabel);
    recordTeamAssignment(assignments, goal?.assist?.player ?? goal?.assist, teamLabel);
    if (teamLabel) {
      const opposite = teamLabel === 'red' || teamLabel === 'Red' ? 'blue' : teamLabel === 'blue' || teamLabel === 'Blue' ? 'red' : null;
      recordTeamAssignment(assignments, goal?.ownGoalPlayer, opposite);
    }
  });

  const passes = Array.isArray(match?.passes) ? match.passes : [];
  passes.forEach((pass) => {
    const teamLabel = pass?.team ?? pass?.lastShot?.team ?? pass?.playerTeam;
    recordTeamAssignment(assignments, pass?.player ?? pass?.lastShot?.player, teamLabel);
    recordTeamAssignment(assignments, pass?.target, teamLabel);
  });

  const saves = Array.isArray(match?.saves) ? match.saves : [];
  saves.forEach((save) => recordTeamAssignment(assignments, save?.player, save?.team));

  const duels = Array.isArray(match?.duels) ? match.duels : [];
  duels.forEach((duel) => {
    recordTeamAssignment(assignments, duel?.player, duel?.team ?? duel?.winnerTeam);
    recordTeamAssignment(assignments, duel?.opponent, duel?.opponentTeam ?? duel?.loserTeam);
    recordTeamAssignment(assignments, duel?.winner, duel?.winnerTeam);
    recordTeamAssignment(assignments, duel?.loser, duel?.loserTeam);
  });

  const simpleLists = [
    match?.clearances,
    match?.clearanceRecoveries,
    match?.reboundRecoveries,
    match?.rebounds,
    match?.interceptions,
    match?.recoveries,
    match?.counterRuns,
    match?.pressures,
    match?.blocks,
    match?.tackles
  ];
  simpleLists.forEach((collection) => {
    if (!Array.isArray(collection)) return;
    collection.forEach((entry) => recordTeamAssignment(assignments, entry?.player ?? entry?.winner ?? entry, entry?.team ?? entry?.teamName ?? entry?.winnerTeam));
  });
}

function recordTeamCollection(assignments, value, team) {
  if (!value) return;
  if (Array.isArray(value)) {
    value.forEach((entry) => recordTeamAssignment(assignments, entry, team));
    return;
  }
  recordTeamAssignment(assignments, value, team);
}

function recordPlayerEntries(assignments, list) {
  if (!Array.isArray(list)) return;
  list.forEach((entry) => {
    const team = resolveEntryTeam(entry);
    recordTeamAssignment(assignments, entry, team);
  });
}

function recordTeamAssignment(assignments, entry, teamLabel) {
  const name = extractPlayerName(entry);
  let team = normalizeTeamValue(teamLabel);
  if (!team) {
    team = resolveEntryTeam(entry);
  }
  if (!name || !team) return;
  const key = name.toLowerCase();
  if (assignments.has(key)) return;
  assignments.set(key, { name, team });
}

function finalizeTeamAssignments(assignments) {
  const red = [];
  const blue = [];
  assignments.forEach(({ name, team }) => {
    if (team === 'red') red.push(name);
    else if (team === 'blue') blue.push(name);
  });
  return {
    red: uniqueNames(red),
    blue: uniqueNames(blue)
  };
}

function fillTeamsFromPlayersList(matches, assignments) {
  const counts = { red: 0, blue: 0 };
  assignments.forEach(({ team }) => {
    if (team === 'red') counts.red += 1;
    else if (team === 'blue') counts.blue += 1;
  });

  const unmatched = [];
  matches.forEach((match) => {
    const roster = Array.isArray(match?.players) ? match.players : Array.isArray(match?.player) ? match.player : [];
    roster.forEach((entry) => {
      const name = extractPlayerName(entry);
      if (!name) return;
      const key = name.toLowerCase();
      if (assignments.has(key)) return;
      unmatched.push({ key, name });
    });
  });

  unmatched.forEach(({ key, name }) => {
    const target = counts.red <= counts.blue ? 'red' : 'blue';
    assignments.set(key, { name, team: target });
    counts[target] += 1;
  });
}

function extractPlayerName(entry) {
  if (!entry) return null;
  if (typeof entry === 'string') return entry.trim();
  if (typeof entry === 'object') {
    const raw = entry.nick || entry.name || entry.player || entry.displayName || entry.username || entry.playerName || entry.Nick || entry.Name;
    return raw ? String(raw).trim() : null;
  }
  return null;
}

function resolveEntryTeam(entry) {
  if (!entry || typeof entry !== 'object') return null;
  const candidates = [
    entry.team,
    entry.teamId,
    entry.teamIndex,
    entry.side,
    entry.colour,
    entry.color,
    entry.squad,
    entry.Team,
    entry.TeamId,
    entry.TeamIndex,
    entry.Side,
    entry.lastTeam,
    entry.teamName,
    entry.TeamName
  ];
  if (entry.team && typeof entry.team === 'object') {
    candidates.push(entry.team.name, entry.team.label, entry.team.slug);
  }
  for (const candidate of candidates) {
    const normalized = normalizeTeamValue(candidate);
    if (normalized) return normalized;
  }
  return null;
}

function normalizeTeamValue(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') {
    if (value === 0) return 'red';
    if (value === 1) return 'blue';
    return null;
  }
  const text = String(value).trim().toLowerCase();
  if (!text) return null;
  if (text === 'red' || text === 'r' || text === 'team1' || text === 'home' || text === 'host' || text === '0' || text === 'redteam' || text === 'red-team' || text === 'red team') {
    return 'red';
  }
  if (text === 'blue' || text === 'b' || text === 'team2' || text === 'away' || text === 'guest' || text === '1' || text === 'blueteam' || text === 'blue-team' || text === 'blue team') {
    return 'blue';
  }
  if (text.includes('red')) return 'red';
  if (text.includes('blue')) return 'blue';
  return null;
}

function uniqueNames(list) {
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  const result = [];
  list.forEach((entry) => {
    const name = typeof entry === 'string' ? entry.trim() : extractPlayerName(entry);
    if (!name) return;
    const key = name.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    result.push(name);
  });
  return result;
}
